import { Box, Divider, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useMemo, useState } from 'react';
import { Button, Dropdown, SearchInput } from '@/shared/ui';
import type { SavedPresetRecord } from '../../model/types';
import { SavedPresetItem } from '../SavedPresetItem/SavedPresetItem';
import { SavedPresetMenu } from '../SavedPresetMenu/SavedPresetMenu';
import { useSavedPresetsContext } from '../SavedPresetsContext/SavedPresetsContext';
import { useSavePresetContext } from '../SavePresetContext/SavePresetContext';
import { SharedSavedPresetMenu } from '../SharedSavedPresetMenu/SharedSavedPresetMenu';

const List = styled('ul')(() => ({
  listStyle: 'none',
  padding: 0,
  margin: 0,
  maxHeight: 300,
  overflowY: 'auto',
  width: 320,
}));

interface Props {
  onItemClick?: (preset: SavedPresetRecord) => void;
}

export function SavedPresetsSelect({ onItemClick }: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const { appliedPresetName, presets, applyPreset } = useSavedPresetsContext();
  const { openSavePresetDialog } = useSavePresetContext();
  const filteredPresets = useMemo(() => {
    return presets.filter((preset) => {
      return preset.name.toLowerCase().includes(query.toLowerCase());
    });
  }, [presets, query]);
  const title = appliedPresetName || 'Select View Preset';

  const handleItemClick = (preset: SavedPresetRecord) => {
    setOpen(false);
    onItemClick?.(preset);
    applyPreset(preset);
  };

  return (
    <Dropdown
      open={open}
      onOpenChange={setOpen}
      label={
        <Box
          component="span"
          sx={{
            maxWidth: 124,
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
          }}
          title={appliedPresetName ? appliedPresetName : ''}
        >
          {title}
        </Box>
      }
      labelProps={{
        sx: {
          borderColor: '#BBBEC8',
          borderRadius: 2,
          borderTopRightRadius: 0,
          borderBottomRightRadius: 0,
          fontWeight: 400,
          height: 40,
          width: 180,
          justifyContent: 'space-between',
          zIndex: 1,
        },
      }}
      paperProps={{
        sx: {
          borderRadius: 2,
          overflow: 'hidden',
          boxShadow: '0px 1px 6px 0px rgba(0, 35, 64, 0.2)',
        },
      }}
      popperProps={{
        placement: 'bottom-start',
        disablePortal: false,
      }}
    >
      <Box sx={{ p: 2 }}>
        <SearchInput query={query} onChange={setQuery} />
      </Box>
      <Divider />
      <List>
        {presets.length === 0 && (
          <Box
            sx={{
              px: 2,
              py: 3,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <svg
              width="33"
              height="32"
              viewBox="0 0 33 32"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M28.5 5.33334H19.1667M13.8333 5.33334H4.5M28.5 16H16.5M11.1667 16H4.5M28.5 26.6667H21.8333M16.5 26.6667H4.5M19.1667 2.66667V8.00001M11.1667 13.3333V18.6667M21.8333 24V29.3333"
                stroke="#7E828F"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
                alignItems: 'center',
              }}
            >
              <Typography
                sx={{
                  fontWeight: 400,
                  fontSize: '0.875rem',
                  lineHeight: '1rem',
                  letterSpacing: 0,
                }}
              >
                No presets saved
              </Typography>
              <Typography
                sx={{
                  fontWeight: 400,
                  fontSize: '0.75rem',
                  lineHeight: '0.875rem',
                  letterSpacing: 0,
                  textAlign: 'center',
                  color: '#7E828F',
                  width: 200,
                }}
              >
                Save filters and settings as a preset for quick access.
              </Typography>
            </Box>
            <Button
              sx={{ py: 0 }}
              variant="text"
              onClick={openSavePresetDialog}
            >
              Save Preset
            </Button>
          </Box>
        )}
        {presets.length > 0 && filteredPresets.length === 0 && (
          <Box sx={{ p: 2, color: '#817E7E', textAlign: 'center' }}>
            No presets found
          </Box>
        )}
        {filteredPresets.map((preset) => {
          return (
            <SavedPresetItem
              key={preset.id}
              presetRecord={preset}
              rightSlot={
                preset.isOwner ? (
                  <SavedPresetMenu presetId={preset.id} />
                ) : (
                  <SharedSavedPresetMenu presetId={preset.id} />
                )
              }
              onClick={handleItemClick}
            />
          );
        })}
      </List>
    </Dropdown>
  );
}
